import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  createWalletRuntime,
  hasPreviousWalletSession,
  type WalletRuntime,
} from "./walletRuntime";

type Wallet = NonNullable<Window["wallet"]>;

interface WalletContextValue {
  wallet?: Wallet;
  connectWallet: () => Promise<void>;
  disconnectWallet: () => Promise<void>;
}

const WalletContext = createContext<WalletContextValue | undefined>(undefined);

export const WalletProvider = ({ children }: React.PropsWithChildren) => {
  const [wallet, setWallet] = useState<Wallet>();
  const runtimeRef = useRef<Promise<WalletRuntime> | null>(null);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  const initializeWallet = useCallback(() => {
    if (!runtimeRef.current) {
      runtimeRef.current = createWalletRuntime()
        .then((runtime) => {
          subscriptionRef.current = runtime.state
            .select("wallets")
            .subscribe((wallets) => setWallet(wallets[0] as Wallet | undefined));
          return runtime;
        })
        .catch((error) => {
          runtimeRef.current = null;
          throw error;
        });
    }

    return runtimeRef.current;
  }, []);

  useEffect(() => {
    if (hasPreviousWalletSession()) {
      void initializeWallet();
    }

    return () => subscriptionRef.current?.unsubscribe();
  }, [initializeWallet]);

  const connectWallet = useCallback(async () => {
    const runtime = await initializeWallet();
    const wallets = await runtime.connectWallet();
    const connectedWallet = wallets[0] as Wallet | undefined;

    if (connectedWallet) {
      window.provider = connectedWallet.provider;
      setWallet(connectedWallet);
    }
  }, [initializeWallet]);

  const disconnectWallet = useCallback(async () => {
    if (!wallet) return;

    const runtime = await initializeWallet();
    await runtime.disconnectWallet({ label: wallet.label });
    window.provider = undefined;
    window.wallet = undefined;
    setWallet(undefined);
  }, [initializeWallet, wallet]);

  const value = useMemo(
    () => ({ wallet, connectWallet, disconnectWallet }),
    [connectWallet, disconnectWallet, wallet]
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};

export const useWalletContext = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error("useWallet must be used within WalletProvider");
  }
  return context;
};
